import gql from 'graphql-tag';
import {
  ConversationThreadMentions,
  ConversationThreadSchema,
} from '../../definitions/schemas';
import { ConversableType } from '../../enums/Conversations';
import Interface from '../Interface';
import { conversationBaseFields } from './ConversationFields';

interface GetConversationThreadsInput {
  conversableId: string;
  conversableType: ConversableType;
}

interface GetConversationThreadsData {
  conversationThreads: ConversationThreadSchema[];
}

interface GetReplySuggestionData {
  getReplySuggestion: {
    message: string;
  };
}

export interface ConversationThreadAttachmentInput {
  filename: string;
  filesize?: number;
  mime: string;
  uri: string;
}

export interface CreateConversationThreadInput {
  attachments?: ConversationThreadAttachmentInput[];
  conversableId: string;
  conversableType: string;
  mentions?: ConversationThreadMentions[];
  message?: string;
  parentId?: string;
}

export interface DeleteConversationThreadInput {
  id: string;
}

interface CreateConversationThreadData {
  createConversationThread: {
    conversationThread: ConversationThreadSchema;
  };
}

export default class ConversationThreadsInterface extends Interface {
  public async get(variables: GetConversationThreadsInput) {
    const response = await this.query<GetConversationThreadsData>(
      () =>
        this.apolloService.executeQuery<GetConversationThreadsData>({
          query: gql`
            query conversationThreads(
              $conversableType: String!
              $conversableId: ID!
            ) {
              conversationThreads(
                conversableType: $conversableType
                conversableId: $conversableId
              ) {
                ${conversationBaseFields}
              }
            }
          `,
          variables,
        }),
      'Error loading conversation messages'
    );

    return response.conversationThreads;
  }

  public async getReplySuggestion(id: string) {
    const response = await this.query<GetReplySuggestionData>(
      () =>
        this.apolloService.executeQuery<GetReplySuggestionData>({
          query: gql`
            query getReplySuggestion($id: ID!) {
              getReplySuggestion(id: $id) {
                message
              }
            }
          `,
          variables: {
            id,
          },
        }),
      'Error loading a reply suggestion'
    );

    return response.getReplySuggestion;
  }

  public async create(input: CreateConversationThreadInput) {
    return this.mutate<CreateConversationThreadData>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation createConversationThread(
            $input: CreateConversationThreadInput!
          ) {
            createConversationThread(input: $input) {
              conversationThread {
                ${conversationBaseFields}
              }
            }
          }
        `,
        variables: {
          input,
        },
      });
      const { errors, message } = response.data.createConversationThread;

      return {
        data: response.data,
        serverErrors: errors,
        serverMessage: message,
      };
    }, 'Error creating the conversation message');
  }

  public delete(input: DeleteConversationThreadInput) {
    return this.mutate<null>(async () => {
      const {
        data: { deleteConversationThread },
      } = await this.apolloService.executeMutation({
        mutation: gql`
          mutation deleteConversationThread(
            $input: DeleteConversationThreadInput!
          ) {
            deleteConversationThread(input: $input) {
              errors
              message
            }
          }
        `,
        variables: { input },
      });
      return {
        data: null,
        serverErrors: deleteConversationThread.errors,
        serverMessage: deleteConversationThread.message,
      };
    }, 'Error deleting content');
  }
}
